import { useReducer } from 'react';
import countries from '../data/countries.json';
import states from '../data/states.json';
import { getRandomId } from '../utils/validation-utils';
import { TRACKING_IDS } from '../constants/lease-abstarction';

const getFilteredStates = (countryName = 'United States') => {
  const found = countries.find(c => c.display_name === countryName);
  return states.filter(s => s.country_id === found?.country_id);
};

const modalFieldsReducer = (currentModalFields, action) => {
  let newValue;
  let updatedFields;
  if (action.type === 'multiple') {
    updatedFields = Object.keys(action.value).reduce((updatedValue, key) => {
      const fieldValue = action.value[key];
      return {
        ...updatedValue,
        [key]: {
          ...currentModalFields[key],
          ...fieldValue,
        },
      };
    }, {});
  }
  switch (action.type) {
    case 'single':
      newValue = {
        ...currentModalFields,
        [action.field]: {
          ...currentModalFields[action.field],
          ...action.value,
        },
      };
      break;

    case 'multiple':
      newValue = {
        ...currentModalFields,
        ...updatedFields,
      };
      break;

    default:
      newValue = {
        ...currentModalFields,
        [action.field]: {
          ...currentModalFields[action.field],
          value: action.value,
        },
      };
      break;
  }
  return newValue;
};

const useFileAbstractionFields = () => {
  const [modalFieldState, dispatchModalFieldState] = useReducer(
    modalFieldsReducer,
    {
      leaseStartDate: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.leaseStartDate,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      isLeaseStartDateValid: true,
      leaseEndDate: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.leaseEndDate,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      isLeaseEndDateValid: true,
      leaseExecutionDate: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.leaseExecutionDate,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      isLeaseExecutionDateValid: true,
      leaseOccupancyDate: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.leaseOccupancyDate,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      isLeaseOccupancyDateValid: true,
      isASublease: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.isASublease,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      propertyName: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.propertyName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      country: {
        value: 'United States',
        extractedValue: '',
        trackingID: TRACKING_IDS.country,
        toolTip: {
          section: 'This is a default value',
          paragraph: '',
          pageIndex: '',
        },
      },
      address: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.address,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      city: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.city,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      stateName: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.stateName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      zip: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.zip,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      isWholeBuilding: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.isWholeBuilding,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      propertyType: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.propertyType,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      parcelSize: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.parcelSize,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      isListedByCBRE: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.isListedByCBRE,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      suite: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.suite,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      floorChange: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.floorChange,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      squareFeet: {
        value: '',
        extractedValue: '',
        trackingID: TRACKING_IDS.squareFeet,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      filteredStatesForProperty: { value: getFilteredStates() },
      tenantCompanyName: {
        value: '',
        trackingID: TRACKING_IDS.tenantCompanyName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantCountryName: {
        value: 'United States',
        trackingID: TRACKING_IDS.tenantCountryName,
        toolTip: {
          section: 'This is a default value',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantAddressLine1: {
        value: '',
        trackingID: TRACKING_IDS.tenantAddressLine1,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantAddressLine2: {
        value: '',
        trackingID: TRACKING_IDS.tenantAddressLine2,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantCity: {
        value: '',
        trackingID: TRACKING_IDS.tenantCity,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantStateName: {
        value: '',
        trackingID: TRACKING_IDS.tenantStateName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantZip: {
        value: '',
        trackingID: TRACKING_IDS.tenantZip,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantPOCFirstName: {
        value: '',
        trackingID: TRACKING_IDS.tenantPOCFirstName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantPOCLastName: {
        value: '',
        trackingID: TRACKING_IDS.tenantPOCLastName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantPOCEmail: {
        value: '',
        trackingID: TRACKING_IDS.tenantPOCEmail,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantPOCPhoneValid: true,
      tenantPOCPhone: {
        value: '',
        trackingID: TRACKING_IDS.tenantPOCPhone,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantPOCJobTitle: {
        value: '',
        trackingID: TRACKING_IDS.tenantPOCJobTitle,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      filteredStatesForTenant: { value: getFilteredStates() },
      landlordCompanyName: {
        value: '',
        trackingID: TRACKING_IDS.landlordCompanyName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordCountryName: {
        value: 'United States',
        trackingID: TRACKING_IDS.landlordCountryName,
        toolTip: {
          section: 'This is a default value',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordAddressLine1: {
        value: '',
        trackingID: TRACKING_IDS.landlordAddressLine1,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordAddressLine2: {
        value: '',
        trackingID: TRACKING_IDS.landlordAddressLine2,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordCity: {
        value: '',
        trackingID: TRACKING_IDS.landlordCity,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordStateName: {
        value: '',
        trackingID: TRACKING_IDS.landlordStateName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordZip: {
        value: '',
        trackingID: TRACKING_IDS.landlordZip,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordPOCFirstName: {
        value: '',
        trackingID: TRACKING_IDS.landlordPOCFirstName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordPOCLastName: {
        value: '',
        trackingID: TRACKING_IDS.landlordPOCLastName,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordPOCEmail: {
        value: '',
        trackingID: TRACKING_IDS.landlordPOCEmail,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordPOCPhoneValid: true,
      landlordPOCPhone: {
        value: '',
        trackingID: TRACKING_IDS.landlordPOCPhone,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordPOCJobTitle: {
        value: '',
        trackingID: TRACKING_IDS.landlordPOCJobTitle,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      filteredStatesForLandlord: { value: getFilteredStates() },
      tenantRepresentCompany: {
        value: '',
        trackingID: TRACKING_IDS.tenantRepresentCompany,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantRepresentAgent: {
        value: '',
        trackingID: TRACKING_IDS.tenantRepresentAgent,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordRepresentCompany: {
        value: '',
        trackingID: TRACKING_IDS.landlordRepresentCompany,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      landlordRepresentAgent: {
        value: '',
        trackingID: TRACKING_IDS.landlordRepresentAgent,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      baseRent: {
        value: '',
        trackingID: TRACKING_IDS.baseRent,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      freeRent: {
        value: '',
        trackingID: TRACKING_IDS.freeRent,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      leaseEscalation: {
        value: 'Fixed',
        trackingID: TRACKING_IDS.leaseEscalation,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      customLeaseEscalation: {
        value: [
          {
            id: getRandomId(),
            rent: {
              is_percent: false,
              rent_dollar: '',
              rent_percent: '',
            },
            months: '',
          },
        ],
        trackingID: TRACKING_IDS.customLeaseEscalation,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      // rentEscalationIsPercent: {
      //   value: '%',
      //   trackingID: 'rent_escalation_is_percent',
      //   toolTip: {
      //     section: '',
      //     paragraph: '',
      //     pageIndex: '',
      //   },
      // },
      // rentEscalationPercent: {
      //   value: '',
      //   trackingID: 'rent_escalation_percent',
      //   toolTip: {
      //     section: '',
      //     paragraph: '',
      //     pageIndex: '',
      //   },
      // },
      // rentEscalationDollar: {
      //   value: '',
      //   trackingID: 'rent_escalation_dollar',
      //   toolTip: {
      //     section: '',
      //     paragraph: '',
      //     pageIndex: '',
      //   },
      // },
      fixedLeaseEscalation: {
        value: {
          rent_escalation_is_percent: false,
          rent_escalation_dollar: 0,
          rent_escalation_percent: 0,
        },
        trackingID: TRACKING_IDS.fixedLeaseEscalation,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantImprovementAllowance: {
        value: '',
        trackingID: TRACKING_IDS.tenantImprovementAllowance,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      parkingRatio: {
        value: '',
        trackingID: TRACKING_IDS.parkingRatio,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      tenantImprovementDetails: {
        value: '',
        trackingID: TRACKING_IDS.tenantImprovementDetails,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      considerationIsPercent: {
        value: 'True',
        trackingID: TRACKING_IDS.considerationIsPercent,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
      customConsideration: {
        value: [
          {
            id: getRandomId(),
            consideration_type: 'Monthly Rent',
            total_units: '',
            amount: '',
            consideration_amount: '',
            commission_percent: '',
            commission_dollars: '',
            commission_amount: '',
          },
        ],
        trackingID: TRACKING_IDS.customConsideration,
        toolTip: {
          section: '',
          paragraph: '',
          pageIndex: '',
        },
      },
    },
  );
  return [modalFieldState, dispatchModalFieldState];
};

export default useFileAbstractionFields;
